import React, {
	useCallback,
	useMemo,
	useState,
} from 'react';
import classNames from 'classnames';
import {
	Checkbox,
	ConfigProvider,
	Flex,
	Form,
	Grid,
} from 'antd';
import {
	DndContext,
	DragEndEvent,
	PointerSensor,
	useSensor,
	useSensors,
} from '@dnd-kit/core';
import {
	arrayMove,
	SortableContext,
	useSortable,
	verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { Table } from 'antd/lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faGasPump } from '@fortawesome/pro-light-svg-icons';
import {
	CurrencySymbols,
	EstimateStatus,
	EstimatorModeTypes,
	PortActionTypes,
	SeaPassageToggles,
	TimeZoneFormat,
	VesselTypes,
} from '@shared/utils/constants';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { formatCurrency } from '@shared/utils/currency';
import { round } from '@shared/utils/math';
import normalizeNumber from '@shared/utils/normalizeNumber';
import {
	formatAndRoundNumber,
	formatNumber,
} from '@shared/utils/formatNumber';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import showErrorNotification from '@client/utils/showErrorNotification';
import Card from '@client/components/Card/Card';
import AddButton from '@client/components/AddButton';
import Select from '@client/components/Select';
import EditableCellTableRedux from '@client/components/EditableTableRedux/EditableCellTableRedux';
import EditableCellRedux from '@client/components/EditableTableRedux/EditableCellRedux';
import BlockableDrawer from '@client/components/BlockableDrawer';
import SimpleForm from '@client/components/SimpleForm';
import CurrencyInput from '@client/components/CurrencyInput';
import { useExchangeRates } from '@client/utils/hooks/useExchangeRates';
import { useSpreadsheetNavigation } from '@client/utils/hooks/useSpreadsheetNavigation';
import SpreadsheetNavigationHint from '@client/components/SpreadsheetNavigationHint';
import Button from '@client/components/Button';
import styles from './styles/shared.module.css';
import { getPortRotationColumns } from './helpers/getPortRotationColumns';
import {
	NewPortRotationEntryFormValues,
	LocalPortRotation,
} from './context/hooks/usePortRotationHandlers';
import { getNewPortRotationEntryFields } from './helpers/getNewPortRotationEntryFields';
import { hasCargoQtyMismatch } from './helpers/hasCargoQtyMismatch';
import { usePortRotationProvider } from './context/PortRotationContext';
import { useEstimate } from './context/EstimatorContext';
import { getPortRotationOtherConsumptionColumns } from './helpers/getPortRotationOtherConsumptionColumns';
import { useCargoProvider } from './context/CargoContext';
import { useEstimateChangesProvider } from './context/EstimateChangesContext';
import { getPortRotationConsumptionColumns } from './helpers/getPortRotationConsumptionColumns';

export interface LocalPortRotationTotals {
	totalDistance: number;
	ecaDistance: number;
	totalSeaDays: number;
	totalIdleDays: number;
	totalWorkingDays: number;
	totalTurnDays: number;
	totalPortCosts: number;
	totalDemDes: number;
}

const { UTC } = TimeZoneFormat;

const Row = ({ ...rest }) => {
	const key = rest['data-row-key'];
	const { componentDisabled } = ConfigProvider.useConfig();

	const [
		_parsedKey,
		_updatedAt,
		disabledStr,
	] = (key ?? '').split(',');

	const disabled = disabledStr === 'true' || componentDisabled;

	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id: key,
		disabled,
	});

	const style: React.CSSProperties = {
		transform: CSS.Translate.toString(transform),
		transition,
		cursor: disabled ? 'default' : 'move',
		...(isDragging ? { position: 'relative', zIndex: 9999, backgroundColor: 'white' } : {}),
	};

	return (
		<tr
			key={key}
			{...rest}
			ref={setNodeRef}
			style={style}
			{...attributes}
			{...listeners}
		/>
	);
};

const extractRowAttributes = (v: string) => {
	const [id, _updatedAt] = v.split(',');

	return {
		id: Number(id),
	};
};

const PortRotationCard = ({
	mode,
	tcEstimate = false,
}: {
	mode: EstimatorModeTypes;
	tcEstimate?: boolean;
}) => {
	const { hasCargoRelet } = useCargoProvider();

	const {
		portRotation,
		onPortRotationChange,
		onPortRotationRowChange,
		onOrderChange,
		onConfigUpdate,
		onDeletePortRotationEntry,
		onAddPortRotationEntry,
		estimateResult,
		ports,
		portOptions,
		consumptionSets,
		defaultConsumptionSetId,
		setSyncPortRotation,
	} = usePortRotationProvider();

	const {
		immediateSaveChanges,
	} = useEstimateChangesProvider();

	const {
		estimate,
		vessel,
		onUpdateEstimate,
		refreshVessel,
	} = useEstimate();

	const { cargos } = useCargoProvider();
	const { componentDisabled } = ConfigProvider.useConfig();

	const [newPortRotationEntryForm] = Form.useForm();
	const [draftingNewPortRotationEntry, setDraftingNewPortRotationEntry] = useState<boolean>(false);
	const [newPortRotationChanged, setNewPortRotationChanged] = useState<boolean>(false);
	const [tempFormValues, setTempFormValues] = useState<Partial<NewPortRotationEntryFormValues>>({});
	const [timeZoneFormat, setTimeZoneFormat] = useState<TimeZoneFormat>(UTC);
	const [speedPopoverOpen, setSpeedPopoverOpen] = useState<number | null>(null);
	const screens = Grid.useBreakpoint();
	const exchangeRates = useExchangeRates(estimateResult?.currency ?? 'USD');
	const [showConsumptionView, setShowConsumptionView] = useState(false);
	const [showOtherConsumptionView, setShowOtherConsumptionView] = useState(false);
	const [requiredArrivalPopoverOpen, setRequiredArrivalPopoverOpen] = useState<number | null>(null);
	const navigationProps = useSpreadsheetNavigation();

	const isFixed = useMemo(() => estimate?.status === EstimateStatus.Fixed, [estimate]);

	const canWaitForLaycan = useMemo(() => {
		let result = true;

		if (cargos.length > 1) {
			result = false;
		}

		if (cargos.length === 1) {
			const cargo = cargos[0];

			if (cargo.loadingPorts.length > 1) {
				result = false;
			}

			if (cargo.dischargePorts.length > 1) {
				result = false;
			}
		}

		if (isFixed) {
			result = false;
		}

		return result;
	}, [cargos]);

	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 1,
			},
		}),
	);

	const onDragEnd = useCallback(async ({ active, over }: DragEndEvent) => {
		if (over == null || isFixed) {
			return;
		}

		const { id: overId } = extractRowAttributes(over.id.toString());
		const { id: activeId } = extractRowAttributes(active.id.toString());

		const overEntry = portRotation.find((p) => p.id === overId);
		const activeEntry = portRotation.find((p) => p.id === activeId);

		if (
			activeEntry?.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ||
		overEntry?.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG
		) {
			showErrorNotification('The ballast leg has to be first');

			return;
		}

		if (
			activeEntry &&
			activeEntry.cargoId === overEntry?.cargoId &&
			overEntry?.type === PortActionTypes.LOADING &&
			activeEntry.type === PortActionTypes.DISCHARGING
		) {
			showErrorNotification('Discharging events cannot be before loading events from the same cargo');

			return;
		}

		if (
			(
				activeEntry?.generatedBy === PortRotationEntryGeneratorTypes.REDELIVERY_PORT &&
				overEntry?.generatedBy === PortRotationEntryGeneratorTypes.DELIVERY_PORT
			) ||
			(
				activeEntry?.generatedBy === PortRotationEntryGeneratorTypes.DELIVERY_PORT &&
				overEntry?.generatedBy === PortRotationEntryGeneratorTypes.REDELIVERY_PORT
			)
		) {
			showErrorNotification('Delivery must be before redelivery');

			return;
		}

		if (active.id !== over?.id) {
			const activeIndex = portRotation.findIndex((i) => i.key === active.id);
			const overIndex = portRotation.findIndex((i) => i.key === over?.id);
			const updated = arrayMove(portRotation, activeIndex, overIndex);
			onOrderChange(updated);
		}
	}, [isFixed, portRotation, onOrderChange]);

	const handlePortRotationEntryDeletion = useCallback((portRotationEntry: LocalPortRotation) => {
		immediateSaveChanges(() => onDeletePortRotationEntry(portRotationEntry));
	}, [immediateSaveChanges, onDeletePortRotationEntry]);

	const handlePortRotationConfigSave = useCallback((id: number, settings: SeaPassageSettings) => {
		immediateSaveChanges(() => {
			onConfigUpdate(id, settings);
			setSyncPortRotation('full');
		});
	}, [immediateSaveChanges, onConfigUpdate]);

	const handleSeaPassageTogglesSave = useCallback((toggles: Partial<SeaPassageToggles>) => {
		immediateSaveChanges(() => {
			onUpdateEstimate({ routeOptions: toggles });
			portRotation.forEach((entry) => onConfigUpdate(entry.id, { ...entry.routeOptions, ...toggles }));
			setSyncPortRotation('full');
		});
	}, [immediateSaveChanges, onUpdateEstimate, portRotation, onConfigUpdate, setSyncPortRotation]);

	const showQtyError = useMemo(
		() => hasCargoQtyMismatch(cargos, portRotation),
		[portRotation, cargos],
	);

	const columns = useMemo(() => getPortRotationColumns({
		showQtyError,
		handlePortRotationEntryDeletion,
		onConfigUpdate,
		handlePortRotationConfigSave,
		handleSeaPassageTogglesSave,
		onPortRotationRowChange,
		speedPopoverOpen,
		setSpeedPopoverOpen,
		currency: estimateResult?.currency ?? 'USD',
		ports: ports ?? [],
		consumptionSets,
		timeZoneFormat,
		screens,
		mode,
		cargos,
		defaultConsumptionSetId,
		disabled: isFixed,
		tcEstimate,
		portRotation,
		onPortRotationChange,
		waitForLaycan: estimate?.waitForLaycan,
		requiredArrivalPopoverOpen,
		setRequiredArrivalPopoverOpen,
		refreshVessel,
		hasCargoRelet,
		vesselFuelGrades: estimate?.vesselFuelGrades ?? [],
	}), [
		showQtyError, handlePortRotationEntryDeletion, onConfigUpdate, onPortRotationRowChange,
		handlePortRotationConfigSave, handleSeaPassageTogglesSave,
		speedPopoverOpen, estimateResult?.currency, ports, consumptionSets,
		timeZoneFormat, screens, mode, cargos, defaultConsumptionSetId, isFixed,
		portRotation, onPortRotationChange, estimate?.waitForLaycan,
		requiredArrivalPopoverOpen, refreshVessel, hasCargoRelet,
		estimate?.vesselFuelGrades
	]);

	const consumptionColumns = useMemo(() => getPortRotationConsumptionColumns({
		handlePortRotationEntryDeletion,
		onConfigUpdate,
		handlePortRotationConfigSave,
		onPortRotationRowChange,
		speedPopoverOpen,
		setSpeedPopoverOpen,
		ports: ports ?? [],
		consumptionSets,
		timeZoneFormat,
		screens,
		mode,
		cargos,
		defaultConsumptionSetId,
		disabled: isFixed,
		portRotation,
		onPortRotationChange,
		requiredArrivalPopoverOpen,
		setRequiredArrivalPopoverOpen,
		refreshVessel,
		hasCargoRelet,
		vesselFuelGrades: estimate?.vesselFuelGrades ?? [],
	}), [handlePortRotationEntryDeletion, onConfigUpdate, handlePortRotationConfigSave, handleSeaPassageTogglesSave,
		onPortRotationRowChange, speedPopoverOpen, ports, consumptionSets,
		timeZoneFormat, screens, mode, cargos, defaultConsumptionSetId, isFixed,
		portRotation, onPortRotationChange,
		requiredArrivalPopoverOpen, refreshVessel, hasCargoRelet,
		estimate?.vesselFuelGrades]);

	const otherConsumptionColumns = useMemo(() => getPortRotationOtherConsumptionColumns({
		onDeletePortRotationEntry,
		onConfigUpdate,
		onPortRotationRowChange,
		speedPopoverOpen,
		setSpeedPopoverOpen,
		currency: estimateResult?.currency ?? 'USD',
		ports: ports ?? [],
		consumptionSets,
		screens,
		vessel,
		disabled: isFixed,
	}), [
		consumptionSets, estimateResult?.currency, isFixed,
		onConfigUpdate, onDeletePortRotationEntry, onPortRotationRowChange,
		ports, screens, speedPopoverOpen, vessel,
	]);

	const totals = useMemo(() => {
		const initialTotals = {
			totalDistance: 0,
			ecaDistance: 0,
			totalSeaDays: 0,
			totalIdleDays: 0,
			totalWorkingDays: 0,
			totalTurnDays: 0,
			totalPortCosts: 0,
			totalLinerCosts: normalizeNumber(estimateResult?.result?.linerCosts),
			totalDemDes: 0,
			qtyLoaded: 0,
		};

		const accumulated = portRotation.reduce((acc, curr) => {
			acc.totalDistance += curr.normalDistance ?? 0;
			acc.ecaDistance += curr.ecaDistance ?? 0;
			acc.totalSeaDays += curr.seaDays ?? 0;
			acc.totalIdleDays += curr.idleDays ?? 0;
			acc.totalWorkingDays += curr.workingDays ?? 0;
			acc.totalTurnDays += curr.turnDays ?? 0;
			acc.totalDemDes += curr.demurrageDespatch ?? 0;

			const portCostCurrency = curr.portCostCurrency ?? estimateResult?.currency;

			if (!portCostCurrency) {
				return acc;
			}

			const exchangeRate = exchangeRates?.[portCostCurrency];

			if (curr.type === PortActionTypes.LOADING) {
				acc.qtyLoaded += normalizeNumber(curr.qty);
			}

			if (exchangeRate == null) {
				acc.totalPortCosts += curr.portCost ?? 0;
			} else {
				const convertedPortCost = (curr.portCost ?? 0) / exchangeRate;
				acc.totalPortCosts += convertedPortCost;
			}

			return acc;
		}, initialTotals);

		const totalPortDays =
			accumulated.totalWorkingDays +
			accumulated.totalIdleDays +
			accumulated.totalTurnDays;

		return {
			...accumulated,
			totalPortDays,
		};
	}, [portRotation, estimateResult, exchangeRates]);

	const tcSummary = useMemo(() => (
		<Table.Summary.Row>
			<Table.Summary.Cell index={0} colSpan={6} />
			<Table.Summary.Cell 
				align="right" 
				className={styles.summaryRow} 
				index={0}
			>
				{formatNumber(totals.totalDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell 
				align="right" 
				className={styles.summaryRow} 
				index={0}
			>
				{formatNumber(totals.ecaDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={3} />
			<Table.Summary.Cell 
				align="right" 
				className={styles.summaryRow} 
				index={0}
			>
				{formatAndRoundNumber(totals.totalSeaDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell 
				align="right" 
				className={classNames(styles.summaryRow, styles.idleTurnGroupStart)}
				index={0}
			>
				{formatAndRoundNumber(totals.totalIdleDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell className={classNames(styles.summaryRow, styles.idleTurnGroupEnd)} index={0}>
				D
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={5} />
			<Table.Summary.Cell index={0}>
				<CurrencyInput
					className={styles.slimInput}
					variant="borderless"
					value={round(totals.totalPortCosts, 2)}
					width={120}
					currency={estimateResult?.currency}
					addCurrencyBefore={false}
					addonBefore={CurrencySymbols[estimateResult?.currency! as keyof typeof CurrencySymbols] ?? estimateResult?.currency}
				/>
			</Table.Summary.Cell>
		</Table.Summary.Row>
	), [totals, estimateResult]);

	const otherConsumptionSummary = useMemo(() => (
		<Table.Summary.Row>
			<Table.Summary.Cell index={0} colSpan={showOtherConsumptionView ? 4 + (vessel?.otherPerformanceEntries.length ?? 0) : 6} />
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatNumber(totals.totalDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatNumber(totals.ecaDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={showOtherConsumptionView ? 2 : 4} />
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalSeaDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalWorkingDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalIdleDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalTurnDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={2} />
			{!showOtherConsumptionView && (
				<>
					<Table.Summary.Cell index={0}>
						<CurrencyInput
							className={styles.slimInput}
							variant="borderless"
							value={totals.totalPortCosts}
							width={120}
							currency={estimateResult?.currency}
							addCurrencyBefore={false}
							addonBefore={
								CurrencySymbols[estimateResult?.currency! as keyof typeof CurrencySymbols] ?? estimateResult?.currency
							}
						/>
					</Table.Summary.Cell>
					<Table.Summary.Cell index={0}>
						<CurrencyInput
							className={styles.slimInput}
							variant="borderless"
							value={totals.totalLinerCosts}
							width={120}
							currency={estimateResult?.currency}
							addCurrencyBefore={false}
							addonBefore={
								CurrencySymbols[estimateResult?.currency! as keyof typeof CurrencySymbols] ?? estimateResult?.currency
							}
						/>
					</Table.Summary.Cell>
					<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
						{formatCurrency(totals.totalDemDes, estimateResult?.currency ?? 'USD')}
					</Table.Summary.Cell>
				</>
			)}
		</Table.Summary.Row>
	), [
		showOtherConsumptionView,
		vessel?.otherPerformanceEntries.length,
		totals,
		estimateResult?.currency,
	]);

	const dndTableSummary = useMemo(() => (
		<Table.Summary.Row>
			<Table.Summary.Cell index={0} colSpan={6} />
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatNumber(totals.totalDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatNumber(totals.ecaDistance)}
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={3} />
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalSeaDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalWorkingDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={classNames(styles.summaryRow, styles.idleTurnGroupStart)} index={0}>
				{formatAndRoundNumber(totals.totalIdleDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.totalTurnDays, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell className={classNames(styles.summaryRow, styles.idleTurnGroupEnd)} index={0}>
				D
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={1} />
			<Table.Summary.Cell align="right" className={styles.summaryRow} index={0}>
				{formatAndRoundNumber(totals.qtyLoaded, 2, 0)}
			</Table.Summary.Cell>
			<Table.Summary.Cell index={0} colSpan={3} />
			{!hasCargoRelet &&
				<>
					<Table.Summary.Cell index={0}>
						<CurrencyInput
							className={styles.slimInput}
							variant="borderless"
							value={round(totals.totalPortCosts, 2)}
							width={120}
							currency={estimateResult?.currency}
							addCurrencyBefore={false}
							addonBefore={
								CurrencySymbols[estimateResult?.currency! as keyof typeof CurrencySymbols] ??
								estimateResult?.currency
							}
						/>
					</Table.Summary.Cell>
					<Table.Summary.Cell index={0}>
						<CurrencyInput
							className={styles.slimInput}
							variant="borderless"
							value={round(totals.totalLinerCosts, 2)}
							width={120}
							currency={estimateResult?.currency}
							addCurrencyBefore={false}
							addonBefore={
								CurrencySymbols[estimateResult?.currency! as keyof typeof CurrencySymbols] ??
								estimateResult?.currency
							}
						/>
					</Table.Summary.Cell>
				</>
			}
		</Table.Summary.Row>
	), [totals, estimateResult?.currency]);

	const otherConsumptionTable = useMemo(() => (
		<EditableCellTableRedux
			components={{
				body: {
					row: Row,
					cell: EditableCellRedux,
				},
			}}
			borderless
			size="small"
			pagination={false}
			bordered
			scroll={{ x: 1250 }}
			rowKey="key"
			columns={otherConsumptionColumns}
			dataSource={portRotation}
			onRowChange={(row) => onPortRotationRowChange(row.id, row)}
			onChange={onPortRotationChange}
			loading={false}
			className={styles.portRotationTable}
			summary={() => <>{otherConsumptionSummary}</>}
		/>
	), [
		otherConsumptionColumns,
		portRotation,
		onPortRotationRowChange,
		onPortRotationChange,
		otherConsumptionSummary,
	]);

	const handleRowChange = useCallback((row: LocalPortRotation) => {
		onPortRotationRowChange(row.id, row);
	}, [onPortRotationRowChange]);

	const consumptionTable = useMemo(() => (
		<DndContext
			sensors={sensors}
			modifiers={[restrictToVerticalAxis]}
			onDragEnd={onDragEnd}
		>
			<SortableContext
				items={portRotation.map((i) => i.key)}
				strategy={verticalListSortingStrategy}
			>
				<EditableCellTableRedux
					components={{
						body: {
							row: Row,
							cell: EditableCellRedux,
						},
					}}
					borderless
					size="small"
					pagination={false}
					bordered
					rowKey="key"
					columns={consumptionColumns}
					dataSource={portRotation}
					onRowChange={handleRowChange}
					onChange={onPortRotationChange}
					loading={false}
					className={styles.portRotationTable}
				/>
			</SortableContext>
		</DndContext>
	), [
		sensors,
		onDragEnd,
		consumptionColumns,
		portRotation,
		handleRowChange,
		onPortRotationChange,
	]);

	const dndTable = useMemo(() => (
		<DndContext
			sensors={sensors}
			modifiers={[restrictToVerticalAxis]}
			onDragEnd={onDragEnd}
		>
			<SortableContext
				items={portRotation.map((i) => i.key)}
				strategy={verticalListSortingStrategy}
			>
				<EditableCellTableRedux
					components={{
						body: {
							row: Row,
							cell: EditableCellRedux,
						},
					}}
					borderless
					size="small"
					pagination={false}
					bordered
					scroll={{ x: tcEstimate ? 1000 : 1550 }}
					rowKey="key"
					columns={columns}
					dataSource={portRotation}
					onRowChange={handleRowChange}
					onChange={onPortRotationChange}
					loading={false}
					className={styles.portRotationTable}
					summary={mode === EstimatorModeTypes.MAP ? undefined : () => <>{tcEstimate ? tcSummary : dndTableSummary}</>}
				/>
			</SortableContext>
		</DndContext>
	), [sensors, onDragEnd, portRotation, columns, handleRowChange, onPortRotationChange, mode, dndTableSummary]);

	const tableContent = useMemo(() => {
		if (showConsumptionView) {
			return consumptionTable;
		}

		if (showOtherConsumptionView) {
			return otherConsumptionTable;
		}

		return dndTable;
	}, [
		showConsumptionView,
		consumptionTable,
		showOtherConsumptionView,
		otherConsumptionTable,
		dndTable,
	]);

	const TIMEZONE_OPTIONS = Object.values(TimeZoneFormat).map((val) => ({ label: val, value: val }));

	return (
		<>
			<Card
				bodyStyle={{ padding: 0 }}
				size="small"
				title={(
					<Flex gap={5} align="center">
						Port Rotation
						<Button
							size="small"
							onClick={() => setShowConsumptionView(!showConsumptionView)}
							icon={(<FontAwesomeIcon icon={faGasPump as IconProp} />)}
						>
							Switch to
							{' '}
							{showConsumptionView ? 'full ' : 'consumption'}
							{' '}
							view
						</Button>

						{(vessel?.type === VesselTypes.CHEMICAL || vessel?.type === VesselTypes.CRUDE_PRODUCT) && (
							<Button
								size="small"
								onClick={() => setShowOtherConsumptionView(!showOtherConsumptionView)}
								icon={(<FontAwesomeIcon icon={faGasPump as IconProp} />)}
							>
								Switch to
								{' '}
								{showOtherConsumptionView ? 'full ' : 'tanker consumption'}
								{' '}
								view
							</Button>
						)}
						<SpreadsheetNavigationHint />
					</Flex>
				)}
				extra={(
					<div className={styles.portRotationExtra}>
						<Checkbox
							checked={estimate?.waitForLaycan}
							disabled={!canWaitForLaycan || componentDisabled}
							onChange={(e) => {
								immediateSaveChanges(() => {
									onUpdateEstimate({ waitForLaycan: e.target.checked });
								});
							}}
						>
							Wait for laycan
						</Checkbox>
						<Select
							size="small"
							onChange={(newValue) => setTimeZoneFormat(newValue as TimeZoneFormat ?? UTC)}
							className={styles.timeFormatSelector}
							defaultValue={UTC}
							options={TIMEZONE_OPTIONS}
						/>
						<AddButton
							size="small"
							onClick={() => setDraftingNewPortRotationEntry(true)}
							disabled={isFixed}
						>
							Add Entry
						</AddButton>
					</div>
				)}
			>
				<div {...navigationProps}>
					{tableContent}
				</div>
			</Card>
			<BlockableDrawer
				title="Add new entry"
				visible={draftingNewPortRotationEntry}
				placement="right"
				onClose={() => setDraftingNewPortRotationEntry(false)}
				changed={newPortRotationChanged}
				width={400}
			>
				<SimpleForm<NewPortRotationEntryFormValues>
					form={newPortRotationEntryForm}
					submitButtonText="Create entry"
					onSubmit={(values) => {
						immediateSaveChanges(() => {
							values.consumptionSetId = Number(defaultConsumptionSetId);
							onAddPortRotationEntry(values, () => setDraftingNewPortRotationEntry(false));
						});
					}}
					onChange={(values) => {
						setTempFormValues((prev) => ({ ...prev, ...values }));
						setNewPortRotationChanged(true);
					}}
					// @ts-ignore
					fields={getNewPortRotationEntryFields({
						portOptions,
						currency: estimateResult?.currency ?? 'USD',
						tempFormValues,
					})}
				/>
			</BlockableDrawer>
		</>
	);
};

export default PortRotationCard;
